// The Lead Overview screen — one business, its agents, and every status with a
// number against it.
//
// WHAT QUESTION THIS ANSWERS, and why it is not the Leads list. The list shows
// rows: this caller, that booking. This shows shape: how many leads, taken by
// which agent, arriving by which route, and where each one stopped. Somebody
// opens the list to find a lead; they open this to find out whether the agents
// are working.
//
// TWO TABLES, NEVER DIVIDED INTO EACH OTHER. Leads come from agent_captures and
// calls from agent_call_events. One call can produce two leads, leads taken
// while the caller was on the line never appear in the call outcomes at all,
// and most calls on this platform predate outcome recording. "Leads per call"
// would look authoritative and mean nothing, so every percentage here stays
// inside one family — lead percentages over leads, call percentages over calls
// that actually carry an outcome.
//
// EVERY STATUS IS SHOWN, INCLUDING AT ZERO. A status with no rows is a fact
// worth reading: "nothing has ever failed" is an answer. Hiding it would mean
// nobody meets the word FAILED until the day one appears, which is the day you
// least want to be learning what it means.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, ArrowRight, Bot, CalendarRange, Inbox, Loader2, PhoneIncoming, RefreshCw,
  ShieldCheck, Tag,
} from 'lucide-react';
import {
  capturesService, BusinessLeadOverview, LeadOverviewAgent, EMPTY_BUSINESS_OVERVIEW,
} from 'api/services/captures.service';
import { STATUS_FAMILIES, familyRows, attentionIn, StatusFamily } from './leadStatuses';
import { count, percent, share, Tone } from './leadGist';
import {
  DateRange, QUICK_RANGES, RangeId, ALL_TIME, rangeFor, rangeIdFor, rangeParams,
  describe as describeRange, isBackwards,
} from './leadDateRange';

/** Tone to the portal's own pill classes, so this screen cannot drift from the rest. */
const PILL: Record<Tone, string> = {
  good: 'tl-pill tl-pill-success',
  ok: 'tl-pill tl-pill-info',
  warn: 'tl-pill tl-pill-amber',
  bad: 'tl-pill tl-pill-danger',
  neutral: 'tl-pill',
};

/** The same tones as a solid fill, for bars. Token-driven, so both themes follow. */
const FILL: Record<Tone, string> = {
  good: 'var(--success-text)',
  ok: 'var(--info-text)',
  warn: 'var(--amber-label)',
  bad: 'var(--danger-text)',
  neutral: 'var(--gray-text)',
};

const dash = <span style={{ color: 'var(--text-muted)' }}>—</span>;

/** A date a person reads. Epoch millis, as everything else on this API sends. */
const day = (ms: number | null): string =>
  ms ? new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

const activeRange = (a: LeadOverviewAgent): string => {
  if (!a.firstLeadAt) return '';
  const from = day(a.firstLeadAt);
  const to = day(a.lastLeadAt);
  return from === to ? from : `${from} – ${to}`;
};

/** One headline figure. */
const Stat: React.FC<{ label: string; value: string; hint?: string; tone?: Tone }> = ({
  label, value, hint, tone,
}) => (
  <div className="min-w-0" title={hint}>
    <div
      className="text-[21px] font-semibold leading-none tabular-nums"
      style={{ color: tone && tone !== 'neutral' ? FILL[tone] : 'var(--text-primary)' }}
    >
      {value}
    </div>
    <div className="text-[11px] mt-1 leading-tight" style={{ color: 'var(--text-muted)' }}>
      {label}
    </div>
  </div>
);

/** A proportional bar. Empty renders as an empty track, never as nothing. */
const Bar: React.FC<{ segments: Array<{ key: string; count: number; tone: Tone; label: string }> }> = ({
  segments,
}) => {
  const total = segments.reduce((n, s) => n + s.count, 0);
  return (
    <div
      className="flex h-2 rounded-full overflow-hidden"
      style={{ background: 'var(--divider)' }}
      role="img"
      aria-label={total === 0 ? 'nothing yet' : segments.filter((s) => s.count)
        .map((s) => `${s.label} ${s.count}`).join(', ')}
    >
      {segments.filter((s) => s.count > 0).map((s) => (
        <div
          key={s.key}
          style={{ width: `${share(s.count, total)}%`, background: FILL[s.tone] }}
          title={`${s.label}: ${count(s.count)}`}
        />
      ))}
    </div>
  );
};

/**
 * One status family: the bar, then a row per status with its meaning and count.
 *
 * The count sits on the row that defines the word deliberately. Splitting them
 * — numbers here, glossary at the bottom — is what the first cut of this screen
 * did, and it meant nobody could tell you what the number they were reading
 * actually counted.
 */
const Family: React.FC<{ family: StatusFamily; counts: Record<string, number>; total: number }> = ({
  family, counts, total,
}) => {
  const rows = familyRows(family, counts);
  const needing = attentionIn(family, counts);

  return (
    <section className="tl-card-flush">
      <header
        className="px-4 py-3 border-b flex items-start justify-between gap-3"
        style={{ borderColor: 'var(--divider)', background: 'var(--surface-raised)' }}
      >
        <div className="min-w-0">
          <h3 className="text-[13.5px] font-semibold" style={{ color: 'var(--text-primary)' }}>
            {family.title}
          </h3>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {family.subtitle}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[11px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
            {count(total)} {family.counts}
          </div>
          {needing > 0 && (
            <div className="text-[11px] font-semibold tabular-nums mt-0.5" style={{ color: 'var(--amber-label)' }}>
              {count(needing)} to chase
            </div>
          )}
        </div>
      </header>

      <div className="px-4 pt-3">
        <Bar segments={rows.map((r) => ({ key: r.key, count: r.count, tone: r.tone, label: r.label }))} />
      </div>

      <ul className="px-4 pb-3 pt-1">
        {rows.map((r) => (
          <li
            key={r.key}
            className="flex items-start gap-3 py-2 border-b last:border-b-0"
            style={{ borderColor: 'var(--divider)' }}
          >
            <span className={`${PILL[r.tone]} shrink-0 mt-0.5`}>{r.label}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] leading-snug" style={{ color: 'var(--text-secondary)' }}>
                {r.meaning}
              </p>
              {r.advice && (
                <p className="text-[11px] mt-0.5 leading-snug" style={{ color: 'var(--text-muted)' }}>
                  {r.advice}
                </p>
              )}
            </div>
            <div className="text-right shrink-0 w-16">
              <div
                className="text-[15px] tabular-nums leading-none"
                style={{
                  color: r.count ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: r.count ? 600 : 400,
                }}
              >
                {count(r.count)}
              </div>
              {total > 0 && r.count > 0 && (
                <div className="text-[10.5px] tabular-nums mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {percent(share(r.count, total))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

const LeadOverview: React.FC = () => {
  const [data, setData] = useState<BusinessLeadOverview>(EMPTY_BUSINESS_OVERVIEW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // All time by default. A screen that opened already filtered would report a
  // fraction of the business as the whole of it, and the only clue would be a
  // lit button somebody did not press.
  const [range, setRange] = useState<DateRange>(ALL_TIME);

  const load = useCallback(async (window: DateRange) => {
    // A backwards window can only ever return nothing, and empty renders
    // exactly like a business with no leads. Refuse it rather than show that.
    if (isBackwards(window)) {
      setError('That range ends before it starts.');
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await capturesService.businessOverview(rangeParams(window));
    if (res.success) {
      setData(res.data || EMPTY_BUSINESS_OVERVIEW);
      setError(null);
    } else {
      setError(res.error || 'Could not load the lead overview');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(range); }, [load, range]);

  // NOT `activeRange` — that is the module-level helper formatting an agent's
  // own date window, and a component-scoped const of the same name shadows it.
  const activeQuick: RangeId = rangeIdFor(range);
  const pick = (id: RangeId) => setRange(rangeFor(id));

  const withoutName = Math.max(0, data.leads - data.withName);
  const high = data.byQuality?.High || 0;

  // Silence is the one figure on this screen worth a sentence: both halves are
  // calls, from the same table, so the division is honest — and a high number
  // is a telephony or greeting problem rather than a prompt one.
  const silent = (data.byOutcome?.NO_CONVERSATION || 0) + (data.byOutcome?.GREETING_ONLY || 0);

  const totalFor = (family: StatusFamily) => (family.counts === 'leads' ? data.leads : data.calls);

  const recoveredUnnamed = useMemo(
    () => data.agents.reduce((n, a) => n + Math.max(0, a.postCall - Math.max(0, a.withName - a.inCall)), 0),
    [data.agents],
  );

  if (loading && !data.organizationId) {
    return (
      <div className="p-6 max-w-7xl">
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Adding up the leads…
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          Lead Overview
        </h1>
        <div className="flex items-center gap-2">
          <Link to="/crm/captures" className="tl-btn tl-btn-neutral tl-btn-sm">
            <Inbox className="w-3.5 h-3.5" /> Open the leads
          </Link>
          <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => load(range)} disabled={loading}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
        {data.name ? <>How <b style={{ color: 'var(--text-secondary)' }}>{data.name}</b>’s voice agents are doing — </> : null}
        how many leads they took, by which route, and where each one stopped.
      </p>

      {/* ── the window ───────────────────────────────────────────────────
          Quick ranges first, because they are what people actually use, and
          the two date boxes after for the case none of them covers. The bar
          stays open rather than folding behind a "Filters" button: this screen
          is a set of totals, and a total whose scope is hidden is a number
          somebody will quote without its caveat. */}
      <div className="tl-card mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold shrink-0"
              style={{ color: 'var(--text-secondary)' }}>
          <CalendarRange className="w-3.5 h-3.5" /> Period
        </span>

        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Quick date ranges">
          {QUICK_RANGES.map((q) => {
            const on = activeQuick === q.id;
            return (
              <button
                key={q.id}
                type="button"
                title={q.hint}
                aria-pressed={on}
                onClick={() => pick(q.id)}
                className={`tl-btn tl-btn-sm ${on ? 'tl-btn-primary' : 'tl-btn-neutral'}`}
              >
                {q.label}
              </button>
            );
          })}
        </div>

        <span className="hidden sm:block h-5 w-px shrink-0" style={{ background: 'var(--border)' }} />

        <div className="flex items-center gap-1.5">
          <input
            id="lead-overview-from"
            type="date"
            aria-label="From"
            value={range.startDate}
            max={range.endDate || undefined}
            onChange={(e) => setRange((r) => ({ ...r, startDate: e.target.value }))}
            className="text-[12px] rounded-lg px-2 py-1.5 border"
            style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-primary)' }}
          />
          <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>to</span>
          <input
            id="lead-overview-to"
            type="date"
            aria-label="To"
            value={range.endDate}
            min={range.startDate || undefined}
            onChange={(e) => setRange((r) => ({ ...r, endDate: e.target.value }))}
            className="text-[12px] rounded-lg px-2 py-1.5 border"
            style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-primary)' }}
          />
        </div>

        {/* What is actually on screen, spelled out — so a figure read off this
            page is never quoted without its period. */}
        <span className="text-[11.5px] ml-auto tabular-nums" style={{ color: 'var(--text-muted)' }}>
          Showing <b style={{ color: 'var(--text-secondary)' }}>{describeRange(range)}</b>
          {loading && <Loader2 className="w-3 h-3 animate-spin inline-block ml-1.5 align-[-2px]" />}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-[12px] mb-4" style={{ color: 'var(--text-muted)' }}>
          <AlertCircle className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--danger-text)' }} />
          <span>{error}</span>
          <button type="button" onClick={() => load(range)} className="underline hover:no-underline">Try again</button>
        </div>
      )}

      {/* ── the headline figures ─────────────────────────────────────────── */}
      <div className="tl-card mb-4">
        <div className="flex flex-wrap gap-x-10 gap-y-4">
          <Stat label="Leads captured" value={count(data.leads)}
                hint="Everything the agents captured — on the call and recovered afterwards." />
          <Stat label="Rated high" value={count(high)} tone={high ? 'good' : 'neutral'}
                hint="Leads the agent itself judged worth chasing." />
          <Stat label="With a name" value={data.leads ? percent(share(data.withName, data.leads)) : '—'}
                tone={data.leads && share(data.withName, data.leads) < 50 ? 'warn' : 'neutral'}
                hint="Measured on the name, not the number: the number is stamped from the call and is never missing." />
          <Stat label="Calls handled" value={count(data.calls)} />
          <Stat label="Calls measured" value={count(data.measured)}
                hint="Calls carrying a recorded outcome. Every call percentage below is taken over these." />
        </div>

        {data.measured > 0 && silent > 0 && (
          <p className="text-[12.5px] mt-4 pt-3 border-t" style={{ color: 'var(--text-secondary)', borderColor: 'var(--divider)' }}>
            <b style={{ color: share(silent, data.measured) >= 25 ? 'var(--danger-text)' : 'var(--text-primary)' }}>
              {count(silent)} of {count(data.measured)}
            </b>{' '}
            measured calls ({percent(share(silent, data.measured))}) never got a word out of the caller — they
            heard the greeting, or nothing was said at all. That is a telephony or greeting problem rather
            than a prompt one.
          </p>
        )}

        {withoutName > 0 && (
          <p className="text-[12.5px] mt-2" style={{ color: 'var(--text-secondary)' }}>
            <b style={{ color: 'var(--text-primary)' }}>{count(withoutName)} leads carry no name.</b>{' '}
            {recoveredUnnamed >= withoutName
              ? 'Every one of them was recovered after the call rather than captured on it — a caller who hangs up early never gives one.'
              : 'A CRM push matches on the phone number, so a lead thinner than the record it meets merges into it.'}
          </p>
        )}
      </div>

      {/* ── agents ───────────────────────────────────────────────────────── */}
      <div className="flex items-baseline gap-2 mb-2">
        <Bot className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
        <h2 className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>Agents</h2>
        <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
          {data.agents.length === 1 ? 'one agent' : `${data.agents.length} agents`} · rows add up to the figures above
        </span>
      </div>

      <div className="tl-card-flush mb-5 overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr style={{ background: 'var(--surface-raised)' }}>
              {['Agent', 'Leads', 'On call / recovered', 'With a name', 'Calls', 'Measured', 'Source', 'Required', 'Active']
                .map((h, i) => (
                  <th
                    key={h}
                    className={`px-3 py-2 text-[10.5px] uppercase tracking-wider font-semibold whitespace-nowrap ${i >= 1 && i <= 5 ? 'text-right' : 'text-left'}`}
                    style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}
                  >
                    {h}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {data.agents.map((a) => {
              const named = a.leads ? share(a.withName, a.leads) : 0;
              return (
                <tr key={a.agentId} style={{ borderBottom: '1px solid var(--divider)' }}>
                  <td className="px-3 py-2.5 min-w-[11rem]">
                    <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>{a.name}</div>
                    <div className="text-[10.5px] mt-0.5" style={{ color: 'var(--text-muted)' }}>agent {a.agentId}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{a.leads ? count(a.leads) : dash}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {a.leads ? (
                      <span className="inline-flex items-center gap-2 justify-end">
                        <span className="inline-flex h-1.5 w-20 rounded-full overflow-hidden" style={{ background: 'var(--divider)' }}>
                          <span style={{ width: `${share(a.inCall, a.leads)}%`, background: FILL.good }} />
                          <span style={{ width: `${share(a.postCall, a.leads)}%`, background: FILL.neutral }} />
                        </span>
                        <span className="tabular-nums text-[11px]" style={{ color: 'var(--text-muted)' }}>
                          {a.inCall} / {a.postCall}
                        </span>
                      </span>
                    ) : dash}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {a.leads ? (
                      <>
                        {count(a.withName)}{' '}
                        <span className="text-[11px]" style={{ color: named < 50 ? 'var(--amber-label)' : 'var(--text-muted)' }}>
                          {percent(named)}
                        </span>
                      </>
                    ) : dash}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{count(a.calls)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums"
                      style={{ color: a.measured ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {count(a.measured)}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {a.leadSource
                      ? <span className="tl-pill tl-pill-info"><Tag className="w-3 h-3" />{a.leadSource}</span>
                      : <span className="tl-pill">not set</span>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {a.requiredFields?.length
                      ? (
                        <span className="inline-flex flex-wrap gap-1">
                          {a.requiredFields.map((f) => (
                            <span key={f} className="tl-pill tl-pill-amber"><ShieldCheck className="w-3 h-3" />{f}</span>
                          ))}
                        </span>
                      )
                      : <span className="tl-pill">not set</span>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {activeRange(a) || dash}
                  </td>
                </tr>
              );
            })}
            {data.agents.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
                  {/* Which of the two this is matters: one is a business to go
                      and set up, the other is a filter to widen. */}
                  {activeQuick === 'all'
                    ? 'No agent has taken a lead or handled a call for this business yet.'
                    : `No agent took a lead or handled a call in ${describeRange(range).toLowerCase()}.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── where the leads came from, by the label they carry ───────────
          A separate question from origin, and the one somebody actually asks:
          how many came from the pricing form and how many from contact. Origin
          cannot answer it — every web lead is WEB whichever form it was. */}
      {Object.keys(data.bySource || {}).length > 0 && (
        <>
          <div className="flex items-baseline gap-2 mb-2">
            <Tag className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
            <h2 className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>
              Where they came from
            </h2>
            <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
              the source each lead arrived with — a label, not a fixed set
            </span>
          </div>
          <div className="tl-card mb-5">
            <Bar segments={Object.entries(data.bySource).map(([key, count], i) => ({
              key, count, label: key,
              tone: (['good', 'ok', 'warn', 'neutral'] as Tone[])[i % 4],
            }))} />
            <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
              {Object.entries(data.bySource).map(([key, n], i) => (
                <li key={key} className="inline-flex items-baseline gap-1.5 text-[12.5px]">
                  <span className="w-2 h-2 rounded-full shrink-0 self-center"
                        style={{ background: FILL[(['good', 'ok', 'warn', 'neutral'] as Tone[])[i % 4]] }} />
                  <span style={{ color: 'var(--text-secondary)' }}>{key}</span>
                  <span className="font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                    {count(n)}
                  </span>
                  <span className="tabular-nums text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {percent(share(n, data.leads))}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] mt-3 pt-2 border-t" style={{ color: 'var(--text-muted)', borderColor: 'var(--divider)' }}>
              Tenant-controlled free text, so it is a label rather than a dimension — production already
              holds “Voice Agent” and “Voice AI” for one thing. To count how a lead reached you, use
              “Where the lead came from” below.
            </p>
          </div>
        </>
      )}

      {/* ── every status, with its number ────────────────────────────────── */}
      <div className="flex items-baseline gap-2 mb-2">
        <PhoneIncoming className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
        <h2 className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>
          Every status, and how many
        </h2>
        <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
          zero-filled — a status with none is still worth reading
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {STATUS_FAMILIES.map((family) => (
          <Family
            key={family.id}
            family={family}
            counts={
              family.id === 'outcome' ? data.byOutcome
              : family.id === 'sync' ? data.bySync
              : family.id === 'origin' ? data.byOrigin
              : family.id === 'pipeline' ? data.byPipelineStatus
              : data.byQuality
            }
            total={totalFor(family)}
          />
        ))}
      </div>

      {data.qualitySpellings?.length > 0 && (
        <p className="text-[11.5px] mt-3" style={{ color: 'var(--text-muted)' }}>
          Counted together despite being written differently by the agent:{' '}
          {data.qualitySpellings.join(', ')}. Worth pinning the wording in the agent’s prompt.
        </p>
      )}

      <div
        className="tl-card mt-5 text-[12.5px]"
        style={{ borderLeft: '3px solid var(--accent)', color: 'var(--text-secondary)' }}
      >
        <b style={{ color: 'var(--text-primary)' }}>
          One figure this screen deliberately does not show: a conversion rate.
        </b>{' '}
        Leads and calls are counted in two different places — one call can produce two leads, leads taken
        on the call never appear in the call outcomes at all, and most calls here predate outcome
        recording. “Leads per call” would look authoritative and mean nothing, so every percentage above
        stays inside one family.
      </div>

      <p className="text-[11.5px] mt-4 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
        Counts only — no caller name, number or anything anybody said appears on this screen.
        <Link to="/crm/captures" className="inline-flex items-center gap-1 hover:underline" style={{ color: 'var(--accent-text)' }}>
          See the leads themselves <ArrowRight className="w-3 h-3" />
        </Link>
      </p>
    </div>
  );
};

export default LeadOverview;
